import 'moment-timezone';

import i18next from 'i18next';
import { isUndefined } from 'lodash';
import type { Moment } from 'moment';
import moment from 'moment';
import durationFormatSetup from 'moment-duration-format';

import { isValidDateTime } from '../../utils/dateHelper';
import { dateTimeFormat, displayTimeFormats, unitOfTime, validTimeFormats } from './types';

durationFormatSetup(moment);

const getLocaleDate = (
  time?: string | Moment | number,
  timeZone?: string,
  keepLocalTime = false,
): Moment =>
  (!isUndefined(time) && time !== '' ? moment.utc(time) : moment()).tz(
    timeZone ?? getTimeZone(),
    keepLocalTime,
  ) as Moment;

export const getLocalDate = (time?: string | Moment | number): Moment =>
  (time ? moment(time) : moment()) as Moment;

export const getUTCDate = (time?: string | Moment | number): Moment =>
  (time ? moment.utc(time) : moment().utc()) as Moment;

const FRACTIONAL_SECONDS_SUFFIX = /\.\d{1,9}$/;

const isNaiveDateTimeWithFractionalSeconds = (time: string) =>
  FRACTIONAL_SECONDS_SUFFIX.test(time) &&
  moment(
    time.replace(FRACTIONAL_SECONDS_SUFFIX, ''),
    dateTimeFormat.SERVER_DATE_TIME_WITH_S_FORMAT,
    true,
  ).isValid();

const checkIfValidDateTime = (time?: string | Moment | number, format?: dateTimeFormat) => {
  return (
    typeof time === 'number' ||
    (typeof time === 'string' && isValidDateTime(time ?? '')) ||
    (typeof time === 'string' && isNaiveDateTimeWithFractionalSeconds(time)) ||
    (format && validTimeFormats?.includes(format))
  );
};

export const getTimeZone = (): string => moment.tz.guess();

export const getTimeZoneAbbreviation = (timeZoneName?: string) =>
  moment()
    .tz(timeZoneName ?? getTimeZone())
    .format('z');

export const getTimeStamp = (
  time?: string | Moment,
  timeZone?: string,
  format?: dateTimeFormat,
): number => {
  const dateObject = getDate(time ?? '', timeZone, format) as Moment;
  return dateObject?.valueOf() as number;
};

export const getDate = (
  time?: string | Moment | number,
  timeZone?: string,
  format?: dateTimeFormat,
  keepLocalTime = false,
): Moment => {
  if (checkIfValidDateTime(time, format)) return getLocaleDate(time ?? '', timeZone, keepLocalTime);
  return getLocalDate(time);
};

export const formatTimeByTimezone = (
  time: string | number,
  timeZone: string,
  format = dateTimeFormat.LOCAL_DATE_FORMAT_TIME,
): string => {
  const dateObject_UTC_to_Tz = getUTCDate(time).tz(timeZone);

  const formattedDate = dateObject_UTC_to_Tz?.format(format) as string;

  if (formattedDate && displayTimeFormats?.includes(format)) {
    return `${formattedDate} ${getTimeZoneAbbreviation(timeZone)}`;
  }
  return formattedDate;
};

export const formatTime = (
  time?: string | Moment | number,
  format = dateTimeFormat.LOCAL_DATE_FORMAT,
  timeZone?: string,
): string => {
  const dateObject = (
    typeof time === 'string' || typeof time === 'number'
      ? getDate(time ?? '', timeZone, format)
      : time
  ) as Moment;

  const formattedDate = dateObject?.format(format) as string;

  if (formattedDate && displayTimeFormats?.includes(format)) {
    return `${formattedDate} ${getTimeZoneAbbreviation(timeZone)}`;
  }
  return formattedDate;
};

export const formatDateSpan = (start?: string, end?: string): string => {
  if (!end) return '';
  const endDate = formatTime(end, dateTimeFormat.LOCAL_DATE_FORMAT);
  if (!start || getDate(start).isSame(getDate(end), 'day')) return endDate;
  return `${formatTime(start, dateTimeFormat.MONTH_FORMAT)} – ${endDate}`;
};

export const formatCalendarDate = (
  date: string,
  format = dateTimeFormat.LOCAL_DATE_FORMAT,
): string => moment(date, dateTimeFormat.SERVER_DATE_FORMAT).format(format);

export const safeFormatTime = (
  time: string | Moment | number | null | undefined,
  format: dateTimeFormat = dateTimeFormat.LOCAL_DATE_FORMAT,
  fallback = '',
  timeZone?: string,
): string => {
  if (time == null || time === '') return fallback;
  const formatted = formatTime(time, format, timeZone);
  return !formatted || formatted.toLowerCase().includes('invalid') ? fallback : formatted;
};

export const todayTime = moment.now();

export const getMaxDate = (date1: Moment, date2: Moment): Moment =>
  moment.max([date1 as Moment, date2]);

export const getDuration = (value: number | undefined, unit: unitOfTime) =>
  moment.duration(value, unit);
//@TODO: fix type
export const substract = (unit: unitOfTime, value: number, date = getDate()): Moment =>
  date.subtract(value, unit);

// True when `time` is within the last `days` (instant comparison, so timezone-agnostic).
// Empty/undefined -> false.
export const isWithinDays = (time: string | number | undefined, days: number): boolean =>
  time ? getDate(time).isAfter(substract(unitOfTime.days, days)) : false;

export const getStartOf = (date: Moment, unit: unitOfTime) => date.startOf(unit);

export const getEndOf = (date: Moment, unit: unitOfTime) => date.endOf(unit);

export const add = (unit: unitOfTime, value: number, date = getDate()): Moment =>
  date.add(value, unit);

export const diff = (
  date2?: Moment | string,
  unit?: unitOfTime,
  date1 = getDate(),
  precise = false,
): number => date1.diff(typeof date2 === 'string' ? getDate(date2) : date2, unit, precise);

export const getTimeOffset = (time?: string | number | Moment, timeZone?: string): number => {
  return moment.tz(getDate(time ?? ''), timeZone ?? getTimeZone()).utcOffset();
};

export const getDateWithLocalOffsetRemoved = (
  time: string | Moment,
  format = dateTimeFormat.DATE_TIME_FORMAT,
): string => {
  return moment(time).subtract(getTimeOffset(time), 'minute').format(format);
};

export const getDateCalendar = (date: string) =>
  getDate(date).calendar(null, {
    lastDay: `[${i18next.t('inputs.dateInput.yesterday')}]`,
    sameDay: `[${i18next.t('inputs.dateInput.today')}]`,
    nextDay: `[${i18next.t('inputs.dateInput.tomorrow')}]`,
    lastWeek: dateTimeFormat.LOCAL_DATE_FORMAT,
    nextWeek: dateTimeFormat.LOCAL_DATE_FORMAT,
    sameElse: dateTimeFormat.LOCAL_DATE_FORMAT,
  });

export function formatTimeSpan(timeSpan?: { start?: string; end?: string }) {
  if (!timeSpan?.start || !timeSpan?.end) return null;

  return {
    start: formatTime(
      getLocaleDate(timeSpan.start, undefined, true).startOf(unitOfTime.day),
      dateTimeFormat.SERVER_DATE_TIME_WITH_S_FORMAT,
    ),
    end: formatTime(
      getLocaleDate(timeSpan.end, undefined, true).endOf(unitOfTime.day),
      dateTimeFormat.SERVER_DATE_TIME_WITH_S_FORMAT,
    ),
  };
}
